import React from "react";
import { Link, useParams } from "react-router-dom";
import { findDish, fullName } from "../data/dishes";
import { articles } from "../data/articles";
import { faqs } from "../data/faqs";
import { photos } from "../data/photos";
import { useTheme, useDocumentTitle, useSite } from "../hooks/useSite";
import FoodImage from "../components/FoodImage";
import DishSwitcher from "../components/DishSwitcher";
import Carousel from "../components/Carousel";
import FaqList from "../components/FaqList";
import ArticleCard from "../components/ArticleCard";
import NotFoundPage from "./NotFoundPage";

export default function DishPage() {
  const { slug } = useParams();
  const dish = findDish(slug);
  const { openModal } = useSite();
  useTheme(dish);
  useDocumentTitle(dish ? fullName(dish) : "Dish not found");
  if (!dish) return <NotFoundPage />;

  const slides = [
    { dish, caption: fullName(dish) },
    { src: dish.photo, alt: `${fullName(dish)} as photographed for the current RollOver menu`, caption: "RollOver menu photography" },
    { src: photos.store1, alt: "Guests at the RollOver counter during an event", caption: "RollOver café" },
  ];
  const related = articles
    .filter((a) => a.dishIds.includes(dish.id))
    .concat(articles)
    .filter((a, i, arr) => arr.findIndex((b) => b.id === a.id) === i)
    .slice(0, 2);

  return (
    <article key={dish.id}>
      <section className="ro-detail-top">
        <div className="ro-container">
          <h1>
            <strong>{dish.name}</strong> <span className="ro-outline">{dish.suffix}</span>
          </h1>
          <div className="ro-detail-main">
            <FoodImage dish={dish} />
            <button type="button" className="ro-pill ro-detail-main__order" onClick={() => openModal("order", { dishId: dish.id })}>
              Order online?
            </button>
          </div>
          <p className="ro-lead">{dish.desc}</p>
          <ul className="ro-five">
            <li>
              <strong>{dish.category}</strong>
              <br />
              category
            </li>
            <li>
              <strong>{dish.price}</strong>
              <br />
              price
            </li>
            <li>
              <strong>{dish.portion}</strong>
              <br />
              serving
            </li>
            <li>
              <strong>{dish.occasion}</strong>
            </li>
          </ul>
          <DishSwitcher mode="links" currentId={dish.id} />
        </div>
      </section>

      <section className="ro-detail-info">
        <div className="ro-container">
          <details className="ro-disclosure">
            <summary>
              <span className="ro-disclosure__show">Show dish details</span>
              <span className="ro-disclosure__hide">Hide dish details</span>
            </summary>
            <div className="ro-information">
              <h2>Ingredients &amp; allergens</h2>
              <p>
                Ask the café about ingredients, allergens and cross-contact before ordering.
              </p>
              <h2>Dish information</h2>
              <table className="ro-info-table">
                <tbody>
                  <tr>
                    <td>Price</td>
                    <td>{dish.price}</td>
                  </tr>
                  <tr>
                    <td>Serving</td>
                    <td>{dish.portion}</td>
                  </tr>
                  <tr>
                    <td>Category</td>
                    <td>{dish.category}</td>
                  </tr>
                </tbody>
              </table>
              <Link className="ro-pill ro-pill--solid" to={dish.itemRoute}>
                {dish.itemRoute.startsWith("/menu/item") ? "View in the full menu" : "See more"}
              </Link>
            </div>
          </details>
        </div>
      </section>

      <section className="ro-section">
        <div className="ro-container">
          <Carousel
            slides={slides}
            label={`${fullName(dish)} gallery`}
            renderSlide={(s) => (s.dish ? <FoodImage dish={s.dish} className="ro-carousel__food" /> : <img src={s.src} alt={s.alt} />)}
          />
        </div>
      </section>

      <section className="ro-section ro-tinted ro-section--compact">
        <div className="ro-container ro-faq-wrap">
          <FaqList items={[faqs[2], faqs[5], faqs[6]]} numbered={false} />
        </div>
      </section>

      <section className="ro-section ro-related">
        <div className="ro-container">
          <h2>
            Featured <strong className="ro-italic">Pairings</strong>
          </h2>
          <div className="ro-related__grid">
            {related.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
          <Link className="ro-pill ro-pill--solid" to="/pairings">
            See all our pairings
          </Link>
        </div>
      </section>
    </article>
  );
}
